/**
 * Owner greeting helper — Patch 24 Part V.
 *
 * Orkexra must NEVER greet the owner with their email address.
 * Priority order:
 *   1. ``preferred_name``  — what the Marketing Department calls them
 *   2. ``display_name``    — formal name from signup
 *   3. First word of display_name
 *   4. ``"there"``         — generic fallback (never leaks any identifier)
 */
export interface GreetableUser {
  preferred_name?: string | null;
  display_name?: string | null;
  name?: string | null;   // legacy auth store shape
  email?: string | null;  // read but NEVER used as greeting output
}

const GENERIC_FALLBACK = "there";

/** Return the conversational name to greet the owner with. */
export function greetingName(u: GreetableUser | null | undefined): string {
  if (!u) return GENERIC_FALLBACK;
  const preferred = (u.preferred_name ?? "").trim();
  if (preferred) return preferred;
  const display = (u.display_name ?? u.name ?? "").trim();
  if (display) {
    const first = display.split(/\s+/)[0];
    if (first) return first;
  }
  return GENERIC_FALLBACK;
}

/** Time-of-day greeting prefix. Deliberately localised to English for now. */
export function timeOfDayGreeting(now: Date = new Date()): string {
  const h = now.getHours();
  if (h < 5)  return "Working late";
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}

/** Full pre-composed greeting: "Good morning, Freon". Never contains an email. */
export function composedGreeting(u: GreetableUser | null | undefined, now?: Date): string {
  return `${timeOfDayGreeting(now)}, ${greetingName(u)}`;
}
